/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// DD-31: main.tsx mixes router/Sentry exports with the React root render call by
// design — separating them would require a significant restructure. The react-refresh
// warning is suppressed here at the file level; all other ESLint rules remain active.
/* eslint-disable react-refresh/only-export-components */

import React, { Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";
import { beforeBreadcrumb } from "@/utils/sentryBreadcrumbs";
import {
  consentTracesSampler,
  installSentryConsent,
} from "@/utils/sentryConsent";
import "./i18n";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  RouteObject,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
// Site typefaces and icons, served from our own domain rather than
// third-party CDNs. See src/static/css/fonts.css.
import "./static/css/fonts.css";
import "./index.css";
// Rules for content published from Django admin (articles, manuals, FAQ
// pages, release notes). Scoped to .ds-rich-content and .ds-table-scroll.
import "./static/css/common/richContent.css";
import App from "./App";
import { URL_LANGUAGES } from "@/lib/constants";
import { installConversionReplayHook } from "@/utils/conversions";
import { installPreloadRecovery } from "@/utils/preloadRecovery";
import { legacyAddressRoutes } from "@/routes/legacyAddressRoutes";
import RedirectKeepingQuery from "@/components/common/RedirectKeepingQuery";

// Lazy load ALL pages for optimal code splitting
const ProductPage = lazy(() => import("./pages/ProductPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));
const TechnicalSupport = lazy(() => import("./pages/support/TechnicalSupport"));
const LostLicenseKey = lazy(() => import("./pages/support/LostLicenseKey"));

const ContactUs = lazy(() => import("./pages/company/ContactUs"));
const AboutDeskshare = lazy(() => import("./pages/company/AboutDeskshare"));
const MediaCoverage = lazy(() => import("./pages/MediaCoverage"));
const WhatsNew = lazy(() => import("./pages/WhatsNew"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Reviews = lazy(() => import("./pages/Reviews"));
const DetailedArticle = lazy(() => import("./pages/support/DetailedArticle"));
const MyPCInfo = lazy(() => import("./pages/MyPCInfo"));
const UserManual = lazy(() => import("./pages/support/UserManual"));
const CameraRequest = lazy(() => import("./pages/CameraRequest"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Faq = lazy(() => import("./pages/support/Faq"));
const PurchasePage = lazy(() => import("./pages/PurchasePage"));
const SecurityMonitorProPurchasePage = lazy(
  () => import("./pages/SecurityMonitorProPurchasePage"),
);
const UninstallFeedback = lazy(() => import("./pages/UninstallFeedback"));
import AuthLayout from "./pages/auth/AuthLayout";
import StandaloneLayout from "./components/layout/StandaloneLayout";
import "./static/css/common/standaloneShell.css";

const AuthLogin = lazy(() => import("./pages/auth/Login"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const OAuthComplete = lazy(() => import("./pages/auth/OAuthComplete"));
const AuthLogout = lazy(() => import("./pages/auth/Logout"));
const AuthForumDisplayName = lazy(
  () => import("./pages/auth/ForumDisplayName"),
);
// Sign-in redirect entry: /:lang/sign-in forwards to merged /auth login.
// Rendered without marketing header/footer (standalone mechanism in App.tsx).
const SignIn = lazy(() => import("./pages/SignIn"));
// Bug 2 resolved: React Articles and VideoTutorialsAndManuals pages enabled.
// Nav links updated to use React Router paths; Traefik redirects the old
// ASP.NET URLs to the new routes — see HANDOFF.md for Sanjeev's action items.
const Articles = lazy(() => import("./pages/resources/Articles"));
const VideoTutorialsAndManuals = lazy(
  () => import("./pages/resources/VideoTutorialsAndManuals"),
);

import { AddLangToPath } from "./helper/AddLanguagePath";
import IPDetails from "./helper/IPDetails";
import { getLanguageFromBrowser } from "@/utils/localizedPath";
import { safeLocalStorage } from "./helper/safeLocalStorage";
import ErrorBoundary from "./ErrorBoundary";
import PageLoader from "./components/common/PageLoader";

// Helper to wrap lazy components with Suspense and ErrorBoundary
const withSuspense = (
  Component: React.LazyExoticComponent<React.ComponentType>,
) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

// Initialise language detection once at app startup.
// IP geolocation is fired here — not on every AddLangToPath() call —
// so the network request happens at most once per session.
// DD-12: URL_LANGUAGES imported from lib/constants — single source of truth
// DD-2: Export the promise so useLanguageInit can await it rather than firing
// a duplicate IPDetails() call while this one is still in-flight.
const initLanguage = async (): Promise<void> => {
  if (!safeLocalStorage.getItem("lang")) {
    // VS-1 (Session 376): ask the browser before asking the network.
    //
    // An IP address tells you where somebody is; navigator.languages tells you
    // what they read. The geolocation lookup below is why a German speaker in
    // France is shown French, and it costs a request on first paint. The
    // browser's own list is free, immediate, and cannot be wrong about that
    // customer. It is consulted only when nothing is stored, so it never
    // overrides a preference the customer chose — see getLanguageFromBrowser
    // for why that ordering is not negotiable.
    const fromBrowser = getLanguageFromBrowser();
    if (fromBrowser) {
      safeLocalStorage.setItem("lang", fromBrowser);
      return;
    }
    try {
      const data = await IPDetails();
      // DD-23: country_code ('BR', 'MX', 'AT', 'CH' etc.) does not map to a language —
      // it maps to a country. Use the 'languages' field (e.g. 'pt-BR') returned by
      // /api/geo/ to extract the primary language code. Affects ~660M first-time visitors.
      let detectedLang = "en";
      if (data?.languages) {
        const primaryTag = data.languages.split(",")[0].trim(); // 'pt-BR'
        const langCode = primaryTag.split("-")[0].toLowerCase(); // 'pt'
        if ((URL_LANGUAGES as readonly string[]).includes(langCode)) {
          detectedLang = langCode;
        }
      } else if (data?.country_code) {
        // Fallback: country_code for simple cases where languages field is absent
        const ipLang = data.country_code.toLowerCase();
        if ((URL_LANGUAGES as readonly string[]).includes(ipLang)) {
          detectedLang = ipLang;
        }
      }
      safeLocalStorage.setItem("lang", detectedLang);
    } catch {
      // Network errors fetching IP data are expected (offline, firewall, etc.)
      safeLocalStorage.setItem("lang", "en");
    }
  }
};

export const languageInitPromise: Promise<void> = initLanguage();

// PH-2a: a tab opened before a release can ask for a page-code file the
// release replaced. Reload once to fetch the new release instead of leaving
// the customer on a blank or broken page. Installed here, at module load,
// so it is listening before the first lazy page is requested. The loop guard
// and what must still not be swallowed are in utils/preloadRecovery.ts.
installPreloadRecovery();

const routerConfig: RouteObject[] = [
  // Old addresses that still arrive: /en/... (English has no prefix) and the
  // old forum index /forum. Outside App and without AddLangToPath on purpose
  // - see routes/legacyAddressRoutes.tsx.
  ...legacyAddressRoutes,
  {
    element: <App />,
    children: [
      {
        path: AddLangToPath("/"),
        element: withSuspense(HomePage),
      },
      {
        path: AddLangToPath("/contact-us"),
        element: withSuspense(ContactUs),
      },
      {
        path: AddLangToPath("/contact_us1.aspx"),
        element: withSuspense(ContactUs),
      },
      {
        path: AddLangToPath("/product-support"),
        element: withSuspense(TechnicalSupport),
      },
      {
        // DD-11: /priority-support appears on every product purchase page but had no route.
        // Redirect to /product-support until a dedicated page is built.
        path: AddLangToPath("/priority-support"),
        element: <Navigate to={AddLangToPath("/product-support")} replace />,
      },
      {
        path: AddLangToPath("/lost-license-key"),
        element: withSuspense(LostLicenseKey),
      },
      {
        path: AddLangToPath("/:productSlug"),
        element: withSuspense(ProductPage),
      },
      {
        path: AddLangToPath("/video-surveillance-software/purchase"),
        element: withSuspense(SecurityMonitorProPurchasePage),
      },
      {
        path: AddLangToPath("/:productSlug/purchase"),
        element: withSuspense(PurchasePage),
      },
      {
        // A versionless address for the newest release notes (DS Controls'
        // "What's New" link after an update): the page lists every release of
        // the product, newest first, and reads only the product slug.
        path: AddLangToPath("/:productSlug/whatsnew"),
        element: withSuspense(WhatsNew),
      },
      {
        path: AddLangToPath("/:productSlug/whatsnew/:whatsNewSlug"),
        element: withSuspense(WhatsNew),
      },
      {
        path: AddLangToPath("/:productSlug/reviews"),
        element: withSuspense(Reviews),
      },
      {
        path: AddLangToPath("/download"),
        element: withSuspense(DownloadPage),
      },
      // /articles route re-enabled — React SPA page (Bug 2)
      { path: AddLangToPath("/articles"), element: withSuspense(Articles) },
      {
        path: AddLangToPath("/:articleSlug/:detailedArticleSlug"),
        element: withSuspense(DetailedArticle),
      },
      // /tutorials-manuals route re-enabled — React SPA page (Bug 2)
      {
        path: AddLangToPath("/tutorials-manuals"),
        element: withSuspense(VideoTutorialsAndManuals),
      },
      {
        path: AddLangToPath("/help/:product_name_slug/:guideSlug"),
        element: withSuspense(UserManual),
      },
      {
        path: AddLangToPath("/faq/:product_name_slug/:guideSlug"),
        element: withSuspense(Faq),
      },
      {
        path: AddLangToPath("/about-deskshare"),
        element: withSuspense(AboutDeskshare),
      },
      {
        path: AddLangToPath("/media-coverage"),
        element: withSuspense(MediaCoverage),
      },
      {
        // AL-3 / R5 (Session 447): My PC Info is standalone. The navigation
        // bar, the cart, Log In and the whole footer sitemap are removed by
        // not being in this shell rather than hidden with a stylesheet rule,
        // which is the fragile way and the way that leaves the markup in the
        // page for a screen reader to walk through.
        //
        // It is recorded here, in the routing table, because that is where
        // the next person asking "which pages are standalone?" will look.
        // App.tsx reads the same list through isStandalonePath() so the
        // marketing header and footer above this shell are suppressed too.
        element: <StandaloneLayout prefix="ds" footerBar />,
        children: [
          {
            path: AddLangToPath("/mypcinfo"),
            element: withSuspense(MyPCInfo),
          },
        ],
      },
      {
        path: AddLangToPath("/camera-request"),
        element: withSuspense(CameraRequest),
      },
      {
        // T-3: the seven auth pages are nested under AuthLayout so every one of
        // them gets the logo that links home. They were flat routes before, so
        // the layout never rendered at all.
        //
        // The child paths are RELATIVE - "forgot-password", not
        // "/auth/forgot-password". Getting that wrong produces routes at
        // /auth/auth/forgot-password, which fail silently as a not-found rather
        // than as an error, so it would not be obvious in a build.
        //
        // The index route replaces what used to be the flat /auth path.
        path: AddLangToPath("/auth"),
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: withSuspense(AuthLogin),
          },
          {
            path: "forgot-password",
            element: withSuspense(ForgotPassword),
          },
          {
            path: "reset-password",
            element: withSuspense(ResetPassword),
          },
          {
            path: "callback",
            element: withSuspense(AuthCallback),
          },
          {
            // T-5: the OAuth browser hand-off lands here to store the allauth
            // session key, then replaces itself with the customer's destination.
            path: "oauth-complete",
            element: withSuspense(OAuthComplete),
          },
          {
            path: "logout",
            element: withSuspense(AuthLogout),
          },
          {
            path: "forum-display-name",
            element: withSuspense(AuthForumDisplayName),
          },
        ],
      },
      {
        path: AddLangToPath("/privacy-policy"),
        element: withSuspense(PrivacyPolicy),
      },
      {
        // Session 515 review, SU-2: one privacy policy for the Site and the
        // Apps. The server answers this old address with a permanent redirect
        // (nginx.conf); this covers a navigation inside the app.
        path: AddLangToPath("/app-privacy-policy"),
        element: <RedirectKeepingQuery to={AddLangToPath("/privacy-policy")} />,
      },
      {
        path: AddLangToPath("/refund-policy"),
        element: withSuspense(RefundPolicy),
      },
      {
        path: AddLangToPath("*"),
        element: withSuspense(NotFound),
      },
    ],
  },
  {
    path: AddLangToPath("/uninstall-feedback"),
    element: withSuspense(UninstallFeedback),
  },
  {
    // Legacy /sign-in entry — forwards to /auth on DeskShare marketing hosts.
    path: AddLangToPath("/sign-in"),
    element: withSuspense(SignIn),
  },
];

// V4: Sentry starts whenever VITE_SENTRY_DSN is set, in any build mode.
// Leave the variable unset in a local .env to opt out.
if (import.meta.env.VITE_SENTRY_DSN) {
  const isDev = import.meta.env.MODE === "development";
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN, // No hardcoded fallback — VITE_SENTRY_DSN must be set in Coolify env vars
    environment: import.meta.env.MODE,
    // Session 515 review, SU-2: page-speed measurements and session replays
    // only with analytics consent (Privacy Policy, Section 5); error reports
    // always. The replay integration is added by installSentryConsent() once
    // analytics is granted, and the sampler answers 0 without it. See
    // src/utils/sentryConsent.ts.
    integrations: [Sentry.browserTracingIntegration()],
    // DD-9: 10% sampling outside development — 1.0 (100%) captures every
    // transaction and exhausts the quota. 0 in development so developer
    // machines do not consume the shared quota.
    tracesSampler: consentTracesSampler(isDev ? 0 : 0.1),
    // DD-28: passive session recording stays off — it is not disclosed in the
    // Privacy Policy. Do not raise this value.
    replaysSessionSampleRate: 0.0,
    // DD-09: error-triggered replay only, reduced from 1.0 to protect quota.
    replaysOnErrorSampleRate: isDev ? 0 : 0.25,
    // U-13: never send a console breadcrumb, and never send the detail of an
    // authentication call. A breadcrumb is the one place a value kept out of
    // the response body and out of the error message can still leave the
    // machine. The rule lives in its own module so it can be tested - see
    // src/utils/sentryBreadcrumbs.ts and src/test/sentryBreadcrumbs.test.ts.
    beforeBreadcrumb,
  });
  installSentryConsent();
}

// createBrowserRouter must be called after Sentry.init so tracing instruments routes correctly
const router = createBrowserRouter(routerConfig);

const initializeApp = (): void => {
  // N-2: hand the conversion replay function to index.html, which calls it from
  // the Ads script's onload. Installed before the first render so a download
  // clicked on the very first painted frame is still counted.
  installConversionReplayHook();

  const rootElement = document.getElementById("root");
  if (rootElement) {
    const root = createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <HelmetProvider>
          {/* Outer Suspense catches the RouterProvider's own lazy boundary during
              initial hydration. PageLoader keeps the UX consistent with per-page
              Suspense boundaries inside withSuspense(). */}
          <Suspense fallback={<PageLoader />}>
            <RouterProvider router={router} />
          </Suspense>
        </HelmetProvider>
      </React.StrictMode>,
    );
  } else {
    // Root element missing — the app cannot mount. Report to Sentry so the
    // deployment failure is visible; there is nothing to render to the user.
    Sentry.captureException(
      new Error("Root element #root not found — app failed to mount"),
      { tags: { context: "initializeApp" } },
    );
  }
};

// ES modules with type="module" are deferred by default.
// The DOM is guaranteed to be ready when this executes since:
// 1. Module scripts are deferred (execute after HTML parsing)
// 2. The script tag is placed at the end of body
// Direct invocation is safer than DOMContentLoaded which may have already fired.
initializeApp();
